import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { IAIPromptTemplate, ISaveAIPromptTemplate } from '../../models/AIPromptTemplate';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { invalidateMyAIPrompts } from './useMyAIPrompts';

interface IMutationOptions<T> {
	impersonationContext?: Api.IImpersonationContext;
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (data: T) => void;
}

/** Saves a prompt. Its own endpoint because a base-typed template body would drop usage. */
export const useSaveAIPrompt = ({
	impersonationContext,
	onError,
	onSuccess,
}: IMutationOptions<IAIPromptTemplate> = {}) => {
	const userSession = useUserSession();

	// Failures arrive as IOperationResultNoValue and there is no MutationCache, so onError has to be explicit.
	return useMutation<IAIPromptTemplate, Api.IOperationResultNoValue, ISaveAIPromptTemplate>({
		mutationFn: prompt => {
			return userSession.webServiceHelper.callAsync<IAIPromptTemplate>(
				Api.ImpersonationBroker.composeApiUrl({ impersonationContext, urlPath: 'template/aiPrompt' }),
				'POST',
				prompt
			);
		},
		onError,
		onSuccess: data => {
			invalidateMyAIPrompts();
			onSuccess?.(data);
		},
	});
};

export const useDeleteAIPrompt = ({ impersonationContext, onError, onSuccess }: IMutationOptions<void> = {}) => {
	const userSession = useUserSession();

	return useMutation<void, Api.IOperationResultNoValue, string>({
		mutationFn: id => {
			return userSession.webServiceHelper.callAsync<void>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `template/${encodeURIComponent(id)}`,
				}),
				'DELETE'
			);
		},
		onError,
		onSuccess: data => {
			invalidateMyAIPrompts();
			onSuccess?.(data);
		},
	});
};

export interface IRenameAIPromptVariables {
	id: string;
	name: string;
}

/** Renaming uses the endpoint every template type shares; it takes the name as a query parameter. */
export const useRenameAIPrompt = ({
	impersonationContext,
	onError,
	onSuccess,
}: IMutationOptions<IAIPromptTemplate> = {}) => {
	const userSession = useUserSession();

	return useMutation<IAIPromptTemplate, Api.IOperationResultNoValue, IRenameAIPromptVariables>({
		mutationFn: ({ id, name }) => {
			return userSession.webServiceHelper.callAsync<IAIPromptTemplate>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					queryParams: { name },
					urlPath: `template/${encodeURIComponent(id)}/name`,
				}),
				'PUT'
			);
		},
		onError,
		onSuccess: data => {
			invalidateMyAIPrompts();
			onSuccess?.(data);
		},
	});
};

export interface IShareAIPromptVariables {
	id: string;
	/** Account shares it with everyone on the account; User keeps it to its creator. */
	scope: Api.TemplateScope.Account | Api.TemplateScope.User;
}

/** Sharing uses the endpoint every template type shares; the server refuses any scope other than these two. */
export const useShareAIPrompt = ({
	impersonationContext,
	onError,
	onSuccess,
}: IMutationOptions<IAIPromptTemplate> = {}) => {
	const userSession = useUserSession();

	return useMutation<IAIPromptTemplate, Api.IOperationResultNoValue, IShareAIPromptVariables>({
		mutationFn: ({ id, scope }) => {
			return userSession.webServiceHelper.callAsync<IAIPromptTemplate>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					queryParams: { scope },
					urlPath: `template/${encodeURIComponent(id)}/scope`,
				}),
				'PATCH'
			);
		},
		onError,
		onSuccess: data => {
			invalidateMyAIPrompts();
			onSuccess?.(data);
		},
	});
};
